import { useEffect, useState } from "react";
import { FaImage, FaVideo } from "react-icons/fa";
import { FiBriefcase, FiEdit, FiPlus } from "react-icons/fi";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CardWrapper,
  CustomDropdown,
  FloorPlan,
  ImageUploadSection,
  InputField,
  InputWithSelect,
  LocationSelector,
  MultiSelectSearch,
  PageHeader,
  PdfUploadSection,
  RadioField,
  SmallInputField,
  SubmitButton,
  TextAreaField,
  VideoUploadSection,
} from "../../../../components/admin/common/form-elements";
import AddResidentialPropertyPageSkeleton from "../../../../components/admin/skeleton-loaders/properties/AddResidentialPropertyPageSkeleton";
import CompanyModal from "../../../../components/modals/CompanyModal";
import api from "../../../../configs/api";
import { useAuth } from "../../../../context/AuthContext";
import { AMENITIES } from "../../../../utils/amenities";
import { INITIAL_LOCATION } from "../../../../utils/constants";
import { showError, showSuccess } from "../../../../utils/toastUtils";

const AddResidentialPropertyPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEditMode = location.pathname.includes("edit");

  const initialFormData = {
    images: [],
    brochureUrl: "",
    videoUrl: "",
    description: "",
    whyToConsider: "",
    floorPlan: "",
    totalFloors: "",
    propertyFloor: "",
    projectName: "",
    brokerage: "1",
    availability: "",
    ownership: "",
    amenities: [],
    location: INITIAL_LOCATION,
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [companyModalMode, setCompanyModalMode] = useState("create");

  const { user } = useAuth();
  const showCompanyDropdown = user?.role === "agent-builder";

  const fetchCompanies = async () => {
    try {
      const res = await api.get(`/companies`);
      setCompanies(res.data?.companies || []);
    } catch (err) {
      console.error("Failed to fetch companies", err);
    }
  };
  useEffect(() => {
    fetchCompanies();
  }, []);

  // FETCH PROPERTY DETAILS IN EDIT MODE
  useEffect(() => {
    const fetchProperty = async () => {
      if (!isEditMode || !id) return;

      try {
        setIsLoading(true);
        const res = await api.get(`/properties/${id}`);
        const data = res.data?.data || res.data;

        if (!data) throw new Error("Property data not found");

        const formattedData = {
          images: data.images || [],
          brochureUrl: data.property?.brochureUrl || "",
          videoUrl: data.videos?.[0] || "",
          description: data.description || "",
          whyToConsider: data.property?.whyToChooose || "",
          floorPlan: {
            floorNumber: data.property?.floorPlan?.floorNumber || "",
            bhk: data.property?.floorPlan?.bhk || "",
            bathrooms: data.property?.floorPlan?.bathrooms || "",
            balcony: data.property?.floorPlan?.balcony || "",
            floorDesign: data.property?.floorPlan?.floorDesignPDF || "",
            carpetArea: data.property?.floorPlan?.carpetArea,
            pricePerSqft: data.property?.floorPlan?.pricePerSqft || "",
            expectedPrice: data.property?.floorPlan?.expectedPrice || "",
            allInclusivePrice:
              data.property?.floorPlan?.conditions?.allInclusivePrice || false,
            priceNegotiable:
              data.property?.floorPlan?.conditions?.priceNegotiable || false,
            taxExcluded:
              data.property?.floorPlan?.conditions?.taxExcluded || false,
          },
          totalFloors: data.property?.floors?.totalFloors || "",
          propertyFloor: data.property?.floors?.propertyFloor || "",
          projectName: data.title || "",
          brokerage: data.property?.brokerage || "1",
          availability: data.property?.availabilityStatus || "",
          ownership: data.property?.ownership || "",
          amenities: data.property?.amenities || [],
          location: data.location || INITIAL_LOCATION,
          companyId: data.property?.companyId || "",
        };
        setSelectedCompany(
          companies.find((c) => c._id === data.property?.companyId) || null
        );
        setFormData(formattedData);
      } catch (error) {
        showError("Failed to load property details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProperty();
  }, [id, isEditMode, companies]);

  // useEffect(() => {
  //   console.log("formdata:", formData);
  // }, []);

  const brokerageOptions = [
    { label: "0% (No Brokerage)", value: "0%" },
    { label: "0.5%", value: "0.5%" },
    { label: "1%", value: "1%" },
    { label: "1.5%", value: "1.5%" },
    { label: "2%", value: "2%" },
    { label: "2.5%", value: "2.5%" },
    { label: "Custom / Negotiable", value: "custom" },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const missing = [];

    if (!formData.projectName?.trim()) missing.push("Project Name");
    if (!formData.location?.coordinates?.length) missing.push("Location");

    if (!formData.images || formData.images.length < 4)
      missing.push("At least 5 Property Images");

    if (!formData.totalFloors || formData.totalFloors <= 0)
      missing.push("Total Floors");

    if (
      !formData.propertyFloor ||
      formData.propertyFloor > formData.totalFloors
    )
      missing.push("Valid Property Floor Number");

    if (!formData.availability) missing.push("Availability Status");

    if (missing.length > 0) {
      const firstMissing = missing[0];
      showError(`Please fill out: ${firstMissing}`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      title: formData?.projectName,
      description: formData?.description,
      type: "residential",
      images: formData?.images,
      videos: [formData?.videoUrl],
      location: formData?.location,
      property: {
        brochureUrl: formData?.brochureUrl,
        whyToChooose: formData?.whyToConsider,
        floors: {
          totalFloors: formData?.totalFloors,
          propertyFloor: formData?.propertyFloor,
        },
        floorPlan: {
          floorNumber: formData?.floorPlan?.floorNumber,
          bhk: formData?.floorPlan?.bhk,
          bathrooms: formData?.floorPlan?.bathrooms,
          balcony: formData?.floorPlan?.balcony,
          floorDesignPDF: formData?.floorPlan?.floorDesign,
          carpetArea: {
            value: formData?.floorPlan?.carpetArea?.value,
            unit: formData?.floorPlan?.carpetArea?.unit,
          },
          pricePerSqft: formData?.floorPlan?.pricePerSqft,
          expectedPrice: formData?.floorPlan?.expectedPrice,
          conditions: {
            allInclusivePrice: formData?.floorPlan?.allInclusivePrice,
            priceNegotiable: formData?.floorPlan?.priceNegotiable,
            taxExcluded: formData?.floorPlan?.taxExcluded,
          },
        },
        brokerage: formData?.brokerage,
        availabilityStatus: formData?.availability,
        ownership: formData?.ownership,
        amenities: formData?.amenities,
        companyId: formData.companyId,
      },
    };

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        await api.put(`/properties/${id}`, payload);
        showSuccess("Property Updated Successfully");
      } else {
        await api.post(`/properties`, payload);
        showSuccess("Property Created Successfully");
      }

      setFormData(initialFormData);
      navigate(-1);
    } catch (error) {
      showError(
        error?.response?.data?.message ||
          (isEditMode
            ? "Property Couldn't be updated"
            : "Property Couldn't be added")
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isEditMode && isLoading) {
    return <AddResidentialPropertyPageSkeleton />;
  }
  return (
    <div className="min-h-screen  bg-background font-poppins">
      {/* Header */}
      <PageHeader title={isEditMode ? "Edit Property" : "Add Property"} />

      {/* Main Content */}
      <div className="max-full mx-auto px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-4">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Project Name */}
            <CardWrapper title={"Project Name"}>
              <InputField
                name={"projectName"}
                value={formData?.projectName}
                placeholder={"Project Name"}
                onChange={handleInputChange}
              />
            </CardWrapper>
            {/* Add Photos */}
            <CardWrapper
              title="Add photos of your property"
              subtitle="A picture is worth a thousand words. 80% of buyers look at photos before buying."
            >
              <ImageUploadSection
                icon={FaImage}
                prefillData={formData?.images}
                buttonText="↓ Add atleast 5 photos"
                description=" Max size 5MB per Image | Formats: png, jpg, webp "
                badge="5"
                onChange={(uploadedImages) => {
                  // console.log("uploadedImages", uploadedImages);
                  setFormData((prev) => ({ ...prev, images: uploadedImages }));
                }}
              />
            </CardWrapper>

            {/* Upload Brochure */}
            <CardWrapper
              title="Upload Brochure"
              subtitle="It provides a clear information about your property and helps agents, buyers or dealers."
            >
              <PdfUploadSection
                prefillData={formData?.brochureUrl}
                onFileSelect={(brochureUrl) => {
                  // console.log("brochureUrl", brochureUrl);
                  setFormData((prev) => ({ ...prev, brochureUrl }));
                }}
              />
            </CardWrapper>
            {/* About you Property */}
            <CardWrapper
              title="About your property (Optional)"
              subtitle="A video is worth a thousand pictures. Properties with videos get higher page views"
            >
              <VideoUploadSection
                icon={FaVideo}
                prefillData={formData.videoUrl ? [formData.videoUrl] : []}
                onFileSelect={(videoUrl) => {
                  // console.log("videoUrl", videoUrl);
                  setFormData((prev) => ({ ...prev, videoUrl }));
                }}
                buttonText="+ Upload Video"
                description="Upload video of min size 20 MB in format .mov, .mp4, .H264, video duration should be less than 10 min`s."
              />
            </CardWrapper>
          </div>

          {/* Middle Column */}
          <div className="space-y-6">
            {/* property Description */}
            <CardWrapper title="Property Description">
              <TextAreaField
                placeholder="Type here..."
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </CardWrapper>
            <CardWrapper title="Why we should consider you ?">
              <TextAreaField
                placeholder="Type here..."
                value={formData?.whyToConsider}
                onChange={(e) =>
                  setFormData({ ...formData, whyToConsider: e.target.value })
                }
              />
            </CardWrapper>
            {/* Floor Details */}
            <CardWrapper
              title={"Floor Details"}
              subtitle={`Total no of floors and your floor details`}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 -mt-2">
                <SmallInputField
                  type="number"
                  value={formData?.totalFloors}
                  name={"totalFloors"}
                  placeholder={"Total Floors"}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      totalFloors: Number(e.target.value),
                    }));
                  }}
                />
                <SmallInputField
                  type="number"
                  value={formData?.propertyFloor}
                  name={"propertyFloor"}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      propertyFloor: Number(e.target.value),
                    }));
                  }}
                  placeholder={"Property on which Floor"}
                />
              </div>
            </CardWrapper>
            {/* Floor Plan */}
            <CardWrapper title={`Floor Plan`}>
              <FloorPlan
                floorPlan={formData.floorPlan}
                onChange={(updatedData) => {
                  // console.log("updatedData:", updatedData);
                  setFormData((prev) => ({
                    ...prev,
                    floorPlan: updatedData?.[0],
                  }));
                }}
                allowMultiple={false}
              />
            </CardWrapper>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {showCompanyDropdown && (
              <CardWrapper title="Select Company">
                <CustomDropdown
                  value={formData.companyId}
                  onChange={(val) => {
                    const selected = companies.find((c) => c._id === val);
                    setSelectedCompany(selected || null);
                    setFormData((prev) => ({ ...prev, companyId: val }));
                  }}
                  options={companies.map((c) => ({
                    value: c._id,
                    logo: c.companyLogo,
                    label: c.companyName,
                  }))}
                  placeholder="Choose existing company..."
                />

                {selectedCompany && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden">
                        {selectedCompany.companyLogo ? (
                          <img
                            alt="Company logo"
                            src={selectedCompany.companyLogo}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <FiBriefcase className="text-gray-400 text-xl" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-semibold">
                          {selectedCompany.companyName}
                        </p>
                        {selectedCompany.reraId && (
                          <p className="text-xs text-gray-500">
                            RERA: {selectedCompany.reraId}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="my-3 text-sm font-medium text-center">OR</div>

                <button
                  type="button"
                  onClick={() => {
                    setCompanyModalMode(selectedCompany ? "edit" : "create");
                    setIsCompanyModalOpen(true);
                  }}
                  className="text-sm flex items-center gap-1 text-primary"
                >
                  <FiEdit className="w-4 h-4" />
                  {selectedCompany
                    ? "Edit Selected Company"
                    : "Create New Company"}
                </button>

                {selectedCompany && (
                  <button
                    type="button"
                    onClick={() => {
                      setCompanyModalMode("create");
                      setIsCompanyModalOpen(true);
                    }}
                    className="mt-2 text-sm flex items-center gap-1 text-primary"
                  >
                    <FiPlus className="w-4 h-4" />
                    Create New Company
                  </button>
                )}

                <CompanyModal
                  open={isCompanyModalOpen}
                  mode={companyModalMode}
                  initialData={
                    companyModalMode === "edit" ? selectedCompany : null
                  }
                  onClose={() => setIsCompanyModalOpen(false)}
                  onSuccess={(company) => {
                    setSelectedCompany(company);
                    setFormData((prev) => ({
                      ...prev,
                      companyId: company._id,
                    }));
                    fetchCompanies();
                  }}
                />
              </CardWrapper>
            )}
            {/* Brokerage */}
            <CardWrapper title={"Brokerage"}>
              <InputWithSelect
                disabled={true}
                selectValue={formData?.brokerage}
                selectOptions={brokerageOptions}
                placeholder={"Select Brokerage"}
                onSelectChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    brokerage: value,
                  }));
                }}
              />
            </CardWrapper>
            {/* Availablity Status */}
            <CardWrapper title="Availability Status">
              <RadioField
                name="availability"
                value={formData?.availability}
                onChange={(value) => {
                  setFormData((prev) => ({ ...prev, availability: value }));
                }}
                options={[
                  { label: "Ready to move", value: "ready_to_move" },
                  { label: "Under Construction", value: "under_construction" },
                ]}
              />
            </CardWrapper>
            {/* Ownership */}
            <CardWrapper title="Ownership">
              <RadioField
                name="ownership"
                value={formData?.ownership}
                onChange={(value) => {
                  setFormData((prev) => ({ ...prev, ownership: value }));
                }}
                options={[
                  { label: "Freehold", value: "freehold" },
                  {
                    label: "Co-operative society",
                    value: "co_operative",
                  },
                  { label: "Leasehold", value: "leasehold" },
                  { label: "Power of attorney", value: "power_of_attorney" },
                ]}
              />
            </CardWrapper>
            {/*Amenities */}
            <CardWrapper title={`Amenities`}>
              <MultiSelectSearch
                value={formData.amenities}
                onChange={(updated) =>
                  setFormData((prev) => ({ ...prev, amenities: updated }))
                }
                items={AMENITIES}
                labelKey="label"
                valueKey="key"
              />
            </CardWrapper>
            {/* Fetch Location */}
            <CardWrapper title="Location">
              <LocationSelector
                value={formData.location}
                onChange={(updatedLocation) =>
                  setFormData((prev) => ({
                    ...prev,
                    location: updatedLocation,
                  }))
                }
              />
            </CardWrapper>
            {/* Submit Button */}
            <SubmitButton
              isLoading={isSubmitting}
              onClick={handleSubmit}
              label={isEditMode ? "Save Changes" : "Add Property"}
            />{" "}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddResidentialPropertyPage;

// import { useState, useEffect } from "react";
// import { useParams, useLocation, useNavigate } from "react-router-dom";
// import { FaVideo, FaImage } from "react-icons/fa";
// import {
//   CardWrapper,
//   VideoUploadSection,
//   ImageUploadSection,
//   InputWithSelect,
//   SubmitButton,
//   PageHeader,
//   SmallInputField,
//   PdfUploadSection,
//   TextAreaField,
//   FloorPlan,
//   RadioField,
//   InputField,
//   MultiSelectSearch,
//   LocationSelector,
// } from "../../../../components/admin/common/form-elements";
// import { showError, showSuccess } from "../../../../utils/toastUtils";
// import api from "../../../../configs/api";
// import { AMENITIES } from "../../../../utils/amenities";
// import AddResidentialPropertyPageSkeleton from "../../../../components/admin/skeleton-loaders/properties/AddResidentialPropertyPageSkeleton";
// import { INITIAL_LOCATION } from "../../../../utils/constants";

// const AddResidentialPropertyPage = () => {
//   const navigate = useNavigate();
//   const { id } = useParams();
//   const location = useLocation();
//   const isEditMode = location.pathname.includes("edit");

//   const initialFormData = {
//     images: [],
//     brochureUrl: "",
//     videoUrl: "",
//     description: "",
//     whyToConsider: "",
//     floorPlan: "",
//     totalFloors: "",
//     propertyFloor: "",
//     projectName: "",
//     builderName: "",
//     brokerage: "1",
//     availability: "",
//     ownership: "",
//     amenities: [],
//     location: INITIAL_LOCATION,
//   };

//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [isLoading, setIsLoading] = useState(false);
//   const [formData, setFormData] = useState(initialFormData);

//   // FETCH PROPERTY DETAILS IN EDIT MODE
//   useEffect(() => {
//     const fetchProperty = async () => {
//       if (!isEditMode || !id) return;

//       try {
//         setIsLoading(true);
//         const res = await api.get(`/properties/${id}`);
//         const data = res.data?.data || res.data;
//         console.log("residential Property:", data);

//         if (!data) throw new Error("Property data not found");

//         const formattedData = {
//           images: data.images || [],
//           brochureUrl: data.property?.brochureUrl || "",
//           videoUrl: data.videos?.[0] || "",
//           description: data.description || "",
//           whyToConsider: data.property?.whyToChooose || "",
//           floorPlan: {
//             floorNumber: data.property?.floorPlan?.floorNumber || "",
//             bhk: data.property?.floorPlan?.bhk || "",
//             bathrooms: data.property?.floorPlan?.bathrooms || "",
//             balcony: data.property?.floorPlan?.balcony || "",
//             floorDesign: data.property?.floorPlan?.floorDesignPDF || "",
//             carpetArea: data.property?.floorPlan?.carpetArea,
//             pricePerSqft: data.property?.floorPlan?.pricePerSqft || "",
//             expectedPrice: data.property?.floorPlan?.expectedPrice || "",
//             allInclusivePrice:
//               data.property?.floorPlan?.conditions?.allInclusivePrice || false,
//             priceNegotiable:
//               data.property?.floorPlan?.conditions?.priceNegotiable || false,
//             taxExcluded:
//               data.property?.floorPlan?.conditions?.taxExcluded || false,
//           },
//           totalFloors: data.property?.floors?.totalFloors || "",
//           propertyFloor: data.property?.floors?.propertyFloor || "",
//           projectName: data.title || "",
//           builderName: data.property?.builderName || "",
//           brokerage: data.property?.brokerage || "1",
//           availability: data.property?.availabilityStatus || "",
//           ownership: data.property?.ownership || "",
//           amenities: data.property?.amenities || [],
//           location: data.location || INITIAL_LOCATION,
//         };

//         setFormData(formattedData);
//       } catch (error) {
//         showError("Failed to load property details");
//       } finally {
//         setIsLoading(false);
//       }
//     };

//     fetchProperty();
//   }, [id, isEditMode]);

//   useEffect(() => {
//     console.log("formdata:", formData);
//   }, []);

//   const brokerageOptions = [
//     { label: "0% (No Brokerage)", value: "0%" },
//     { label: "0.5%", value: "0.5%" },
//     { label: "1%", value: "1%" },
//     { label: "1.5%", value: "1.5%" },
//     { label: "2%", value: "2%" },
//     { label: "2.5%", value: "2.5%" },
//     { label: "Custom / Negotiable", value: "custom" },
//   ];

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({
//       ...prev,
//       [name]: value,
//     }));
//   };

//   const validateForm = () => {
//     const missing = [];

//     if (!formData.projectName?.trim()) missing.push("Project Name");
//     if (!formData.location?.coordinates?.length) missing.push("Location");

//     if (!formData.images || formData.images.length < 4)
//       missing.push("At least 5 Property Images");

//     if (!formData.totalFloors || formData.totalFloors <= 0)
//       missing.push("Total Floors");

//     if (
//       !formData.propertyFloor ||
//       formData.propertyFloor > formData.totalFloors
//     )
//       missing.push("Valid Property Floor Number");

//     if (!formData.availability) missing.push("Availability Status");

//     if (missing.length > 0) {
//       const firstMissing = missing[0];
//       showError(`Please fill out: ${firstMissing}`);
//       return false;
//     }
//     return true;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!validateForm()) return;

//     const payload = {
//       title: formData?.projectName,
//       description: formData?.description,
//       type: "residential",
//       images: formData?.images,
//       videos: [formData?.videoUrl],
//       location: formData?.location,
//       property: {
//         brochureUrl: formData?.brochureUrl,
//         whyToChooose: formData?.whyToConsider,
//         floors: {
//           totalFloors: formData?.totalFloors,
//           propertyFloor: formData?.propertyFloor,
//         },
//         floorPlan: {
//           floorNumber: formData?.floorPlan?.floorNumber,
//           bhk: formData?.floorPlan?.bhk,
//           bathrooms: formData?.floorPlan?.bathrooms,
//           balcony: formData?.floorPlan?.balcony,
//           floorDesignPDF: formData?.floorPlan?.floorDesign,
//           carpetArea: {
//             value: formData?.floorPlan?.carpetArea?.value,
//             unit: formData?.floorPlan?.carpetArea?.unit,
//           },
//           pricePerSqft: formData?.floorPlan?.pricePerSqft,
//           expectedPrice: formData?.floorPlan?.expectedPrice,
//           conditions: {
//             allInclusivePrice: formData?.floorPlan?.allInclusivePrice,
//             priceNegotiable: formData?.floorPlan?.priceNegotiable,
//             taxExcluded: formData?.floorPlan?.taxExcluded,
//           },
//         },
//         builderName: formData?.builderName,
//         brokerage: formData?.brokerage,
//         availabilityStatus: formData?.availability,
//         ownership: formData?.ownership,
//         amenities: formData?.amenities,
//       },
//     };

//     setIsSubmitting(true);

//     try {
//       if (isEditMode) {
//         await api.put(`/properties/${id}`, payload);
//         showSuccess("Property Updated Successfully");
//       } else {
//         await api.post(`/properties`, payload);
//         showSuccess("Property Created Successfully");
//       }

//       setFormData(initialFormData);
//       navigate(-1);
//     } catch (error) {
//       showError(
//         error?.response?.data?.message ||
//           (isEditMode
//             ? "Property Couldn't be updated"
//             : "Property Couldn't be added")
//       );
//     } finally {
//       setIsSubmitting(false);
//     }
//   };
//   if (isEditMode && isLoading) {
//     return <AddResidentialPropertyPageSkeleton />;
//   }
//   return (
//     <div className="min-h-screen  bg-background font-poppins">
//       {/* Header */}
//       <PageHeader title={isEditMode ? "Edit Property" : "Add Property"} />

//       {/* Main Content */}
//       <div className="max-full mx-auto px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6">
//         <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-4">
//           {/* Left Column */}
//           <div className="space-y-6">
//             {/* Project Name */}
//             <CardWrapper title={"Project Name"}>
//               <InputField
//                 name={"projectName"}
//                 value={formData?.projectName}
//                 placeholder={"Project Name"}
//                 onChange={handleInputChange}
//               />
//             </CardWrapper>
//             {/* Add Photos */}
//             <CardWrapper
//               title="Add photos of your property"
//               subtitle="A picture is worth a thousand words. 80% of buyers look at photos before buying."
//             >
//               <ImageUploadSection
//                 icon={FaImage}
//                 prefillData={formData?.images}
//                 buttonText="↓ Add atleast 5 photos"
//                 description=" Max size 5MB per Image | Formats: png, jpg, webp "
//                 badge="5"
//                 onChange={(uploadedImages) => {
//                   // console.log("uploadedImages", uploadedImages);
//                   setFormData((prev) => ({ ...prev, images: uploadedImages }));
//                 }}
//               />
//             </CardWrapper>

//             {/* Upload Brochure */}
//             <CardWrapper
//               title="Upload Brochure"
//               subtitle="It provides a clear information about your property and helps agents, buyers or dealers."
//             >
//               <PdfUploadSection
//                 prefillData={formData?.brochureUrl}
//                 onFileSelect={(brochureUrl) => {
//                   // console.log("brochureUrl", brochureUrl);
//                   setFormData((prev) => ({ ...prev, brochureUrl }));
//                 }}
//               />
//             </CardWrapper>
//             {/* About you Property */}
//             <CardWrapper
//               title="About your property (Optional)"
//               subtitle="A video is worth a thousand pictures. Properties with videos get higher page views"
//             >
//               <VideoUploadSection
//                 icon={FaVideo}
//                 prefillData={formData.videoUrl ? [formData.videoUrl] : []}
//                 onFileSelect={(videoUrl) => {
//                   // console.log("videoUrl", videoUrl);
//                   setFormData((prev) => ({ ...prev, videoUrl }));
//                 }}
//                 buttonText="+ Upload Video"
//                 description="Upload video of min size 20 MB in format .mov, .mp4, .H264, video duration should be less than 10 min`s."
//               />
//             </CardWrapper>
//           </div>

//           {/* Middle Column */}
//           <div className="space-y-6">
//             {/* property Description */}
//             <CardWrapper title="Property Description">
//               <TextAreaField
//                 placeholder="Type here..."
//                 value={formData.description}
//                 onChange={(e) =>
//                   setFormData({ ...formData, description: e.target.value })
//                 }
//               />
//             </CardWrapper>
//             <CardWrapper title="Why we should consider you ?">
//               <TextAreaField
//                 placeholder="Type here..."
//                 value={formData?.whyToConsider}
//                 onChange={(e) =>
//                   setFormData({ ...formData, whyToConsider: e.target.value })
//                 }
//               />
//             </CardWrapper>
//             {/* Floor Details */}
//             <CardWrapper
//               title={"Floor Details"}
//               subtitle={`Total no of floors and your floor details`}
//             >
//               <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 -mt-2">
//                 <SmallInputField
//                   type="number"
//                   value={formData?.totalFloors}
//                   name={"totalFloors"}
//                   placeholder={"Total Floors"}
//                   onChange={(e) => {
//                     setFormData((prev) => ({
//                       ...prev,
//                       totalFloors: Number(e.target.value),
//                     }));
//                   }}
//                 />
//                 <SmallInputField
//                   type="number"
//                   value={formData?.propertyFloor}
//                   name={"propertyFloor"}
//                   onChange={(e) => {
//                     setFormData((prev) => ({
//                       ...prev,
//                       propertyFloor: Number(e.target.value),
//                     }));
//                   }}
//                   placeholder={"Property on which Floor"}
//                 />
//               </div>
//             </CardWrapper>
//             {/* Floor Plan */}
//             <CardWrapper title={`Floor Plan`}>
//               <FloorPlan
//                 floorPlan={formData.floorPlan}
//                 onChange={(updatedData) => {
//                   // console.log("updatedData:", updatedData);
//                   setFormData((prev) => ({
//                     ...prev,
//                     floorPlan: updatedData?.[0],
//                   }));
//                 }}
//                 allowMultiple={false}
//               />
//             </CardWrapper>
//           </div>

//           {/* Right Column */}
//           <div className="space-y-6">
//             {/* Builder Name */}
//             <CardWrapper title={"Builder Name"}>
//               <InputField
//                 name={"builderName"}
//                 value={formData?.builderName}
//                 placeholder={"Builder Name"}
//                 onChange={handleInputChange}
//               />
//             </CardWrapper>
//             {/* Brokerage */}
//             <CardWrapper title={"Brokerage"}>
//               <InputWithSelect
//                 disabled={true}
//                 selectValue={formData?.brokerage}
//                 selectOptions={brokerageOptions}
//                 placeholder={"Select Brokerage"}
//                 onSelectChange={(value) => {
//                   setFormData((prev) => ({
//                     ...prev,
//                     brokerage: value,
//                   }));
//                 }}
//               />
//             </CardWrapper>
//             {/* Availablity Status */}
//             <CardWrapper title="Availability Status">
//               <RadioField
//                 name="availability"
//                 value={formData?.availability}
//                 onChange={(value) => {
//                   setFormData((prev) => ({ ...prev, availability: value }));
//                 }}
//                 options={[
//                   { label: "Ready to move", value: "ready_to_move" },
//                   { label: "Under Construction", value: "under_construction" },
//                 ]}
//               />
//             </CardWrapper>
//             {/* Ownership */}
//             <CardWrapper title="Ownership">
//               <RadioField
//                 name="ownership"
//                 value={formData?.ownership}
//                 onChange={(value) => {
//                   setFormData((prev) => ({ ...prev, ownership: value }));
//                 }}
//                 options={[
//                   { label: "Freehold", value: "freehold" },
//                   {
//                     label: "Co-operative society",
//                     value: "co_operative",
//                   },
//                   { label: "Leasehold", value: "leasehold" },
//                   { label: "Power of attorney", value: "power_of_attorney" },
//                 ]}
//               />
//             </CardWrapper>
//             {/*Amenities */}
//             <CardWrapper title={`Amenities`}>
//               <MultiSelectSearch
//                 value={formData.amenities}
//                 onChange={(updated) =>
//                   setFormData((prev) => ({ ...prev, amenities: updated }))
//                 }
//                 items={AMENITIES}
//                 labelKey="label"
//                 valueKey="key"
//               />
//             </CardWrapper>
//             {/* Fetch Location */}
//             <CardWrapper title="Location">
//               <LocationSelector
//                 value={formData.location}
//                 onChange={(updatedLocation) =>
//                   setFormData((prev) => ({
//                     ...prev,
//                     location: updatedLocation,
//                   }))
//                 }
//               />
//             </CardWrapper>
//             {/* Submit Button */}
//             <SubmitButton
//               isLoading={isSubmitting}
//               onClick={handleSubmit}
//               label={isEditMode ? "Save Changes" : "Add Property"}
//             />{" "}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AddResidentialPropertyPage;
